// OPTIONS · Diagnostics (advanced users and admins). Everything an engineer or an experienced trader
// needs when something is wrong, in one place: the full readiness matrix (SHADOW / LIVE) with option
// quantity verification, the scanner feed and its safety gate, the main trading connection's
// option store, the NSE holiday calendar, and the per-pair Validation table. Nothing here trades.
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { ScannerStatusBar, usePoll } from "@/components/options/OptionsCommon";
import ReadinessDetails from "@/components/options/ReadinessDetails";
import OptionsValidation from "@/pages/options/OptionsValidation";
import { useAdvanced } from "@/lib/advanced";
import { useOptionsEnvironment } from "@/lib/optionsEnv";
import { sharedGet, visibleInterval } from "@/lib/poll";

const TABS = [["readiness", "Readiness"], ["feeds", "Feeds & connections"], ["calendar", "Calendar"], ["validation", "Validation"]];
const btn = "px-2 py-1 rounded-sm text-xs font-bold border";

function Row({ k, v }) {
  return <tr className="border-t border-[var(--term-border)]"><td className="px-2 py-1 text-[var(--term-muted)]">{k}</td><td className="px-2 py-1 mono">{v === null || v === undefined ? "—" : String(v)}</td></tr>;
}

function Feeds() {
  const scanner = usePoll("/status", { ms: 5000 });
  const [main, setMain] = useState(null);
  const [hedge, setHedge] = useState(null);
  const load = useCallback(() => {
    sharedGet("/zerodha/market-diagnostics").then((r) => setMain(r.data)).catch(() => setMain(null));
    api.get("/options-hedge/status").then((r) => setHedge(r.data)).catch(() => setHedge(null));
  }, []);
  useEffect(() => { load(); return visibleInterval(load, 5000); }, [load]);
  return (
    <div className="space-y-3">
      <div className="space-y-1"><b className="text-xs">Options Analysis scanner (separate service)</b>
        <ScannerStatusBar status={scanner.data} onChanged={scanner.reload} /></div>
      <div className="term-panel rounded-sm p-3 text-xs" data-testid="diag-main-connection">
        <b>Main trading connection (Zerodha)</b>
        <table className="mt-1"><tbody>
          <Row k="Session" v={main?.zerodha_status} />
          <Row k="WebSocket connected" v={main?.ws_is_connected} />
          <Row k="Trading products" v={main?.trading_products} />
          <Row k="Futures tokens subscribed" v={main?.tokens_requested} />
          <Row k="Option execution tokens" v={main?.option_tokens} />
          <Row k="Option ticks received" v={main?.option_tick_count} />
          <Row k="Futures subscriptions refused (product mode)" v={main?.futures_subscribe_refused} />
          <Row k="Order updates received" v={main?.order_updates_received} />
          <Row k="Market status (futures data)" v={main?.market_status} />
          <Row k="Option market data source" v={hedge?.leg_feed === "MAIN" ? "main connection" : hedge?.leg_feed === "SEPARATE" ? "separate connection (rollback)" : null} />
          <Row k="Broker writes" v={hedge ? (hedge.broker_writes_enabled ? "ON" : "OFF") : null} />
          <Row k="SHADOW server switch" v={hedge ? (hedge.shadow_trading_enabled ? "ON" : "OFF") : null} />
          <Row k="Options LIVE server switch" v={hedge ? (hedge.options_live_enabled ? "ON" : "OFF") : null} />
        </tbody></table>
        <div className="mt-2 flex gap-3 text-[11px]"><Link className="underline" to="/broker">Broker Session · reconciliation and risk controls</Link>
          <Link className="underline" to="/shadow-live">SHADOW LIVE ledger and halts</Link></div>
      </div>
    </div>
  );
}

function Calendar() {
  const [c, setC] = useState(null);
  const load = () => api.get("/options-hedge/calendar").then((r) => setC(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);
  const refresh = async () => {
    try { const r = await api.post("/options-hedge/calendar/refresh"); toast.success(`Refreshed: ${r.data.holidays} holidays (${r.data.years.join(", ")})`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  if (!c) return null;
  return (
    <div className="term-panel rounded-sm p-3 text-xs space-y-2" data-testid="diag-calendar">
      <div className="flex items-center gap-2 flex-wrap">
        <b>NFO trading calendar</b>
        <span className={c.covers_this_year ? "text-[#008a04]" : "text-[#FF3B30]"}>{c.covers_this_year ? "covers this year" : "DOES NOT cover this year"}</span>
        <span className="mono text-[10px] text-[var(--term-muted)]">years {(c.years || []).join(", ")} · source {c.source} · fetched {c.fetched_at}</span>
        <button className={`${btn} ml-auto`} onClick={refresh} data-testid="diag-calendar-refresh">Refresh from NSE</button>
      </div>
      <div className="mono text-[11px] flex flex-wrap gap-x-3 gap-y-1">{(c.holiday_dates || []).map((d) => <span key={d}>{d}</span>)}</div>
      <div className="text-[10px] text-[var(--term-muted)]">Used by the entry gate (no new trades on a holiday) and the pre-expiry exit (counted in trading days).</div>
    </div>
  );
}

export default function OptionsDiagnostics() {
  const [advanced, setAdvanced] = useAdvanced();
  const env = useOptionsEnvironment();
  const [tab, setTab] = useState("readiness");
  return (
    <div className="p-4 space-y-3" data-testid="options-diagnostics-page">
      <div className="flex items-center gap-2 flex-wrap">
        <h1 className="text-lg font-bold flex items-center gap-2"><ClipboardCheck className="h-5 w-5" /> Options Diagnostics</h1>
        <span className="text-xs text-[var(--term-muted)]">readiness, feeds, calendar and validation — for when something needs a closer look</span>
        <label className="ml-auto flex items-center gap-1 text-xs"><input type="checkbox" checked={advanced} onChange={(e) => setAdvanced(e.target.checked)} data-testid="diag-advanced" /> Show advanced details on Options screens</label>
      </div>
      <div className="flex gap-1 border-b border-[var(--term-border)]">
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`diag-tab-${k}`}
            className={`px-3 py-1.5 text-xs font-bold border-b-2 ${tab === k ? "border-[#2563EB] text-[var(--term-primary)]" : "border-transparent text-[var(--term-muted)]"}`}>{label}</button>
        ))}
      </div>
      {tab === "readiness" && env !== undefined && <ReadinessDetails initialEnv={env || "SHADOW"} />}
      {tab === "feeds" && <Feeds />}
      {tab === "calendar" && <Calendar />}
      {tab === "validation" && <OptionsValidation />}
    </div>
  );
}
